import { useCurrentUser } from '@/features/identity';
import { FOCUS_RING } from '@/shared/components/pillClasses';

import {
  activeBuiltInView,
  builtInView,
  type BuiltInView,
  type TaskListView,
} from '../listView';

const LABEL: Record<BuiltInView, string> = {
  all: 'All',
  mine: 'Mine',
  'needs-me': 'Needs me',
};

const ORDER: readonly BuiltInView[] = ['all', 'mine', 'needs-me'];

/**
 * The two questions asked most — "what is mine?" and "what is waiting on
 * me?" — one press each (spec 114 US1).
 *
 * Presets, not a new kind of state: pressing one writes ordinary filters to
 * the address, and which one is pressed is read back from those filters by
 * `activeBuiltInView`. So a shared link shows the same view pressed, and the
 * moment the filters stop matching — a tile, another filter, one chip removed
 * — no view is pressed, rather than one claiming a list it no longer lists.
 *
 * Mine and Needs me wait for the session: until the member is known, "me"
 * narrows nothing, and a pressed "Mine" over everyone's tasks would be a lie.
 */
export function TaskListViews({
  view,
  onViewChange,
}: {
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
}) {
  const me = useCurrentUser().data?.id;
  const active = activeBuiltInView(view);

  return (
    <div
      role="group"
      aria-label="Views"
      // On a phone the three split the line the count ends (spec 271).
      className="border-input inline-flex h-7 shrink-0 items-center gap-0.5 rounded-lg border p-0.5 pointer-coarse:h-auto max-md:-order-2 max-md:flex-1"
    >
      {ORDER.map((key) => {
        const pressed = active === key;
        const waiting = key !== 'all' && me === undefined;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={pressed}
            disabled={waiting}
            title={waiting ? 'Signing in…' : undefined}
            onClick={() => {
              onViewChange(builtInView(key, view));
            }}
            className={`touch-target h-full rounded-md px-2.5 text-xs whitespace-nowrap max-md:flex-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING} ${
              pressed
                ? 'bg-muted text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {LABEL[key]}
          </button>
        );
      })}
    </div>
  );
}
